import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Check, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import { ZONE_LABELS } from "@/components/CoverageDiagram";
import PpfPopular from "@/components/PpfPopular";

/**
 * PPF coverage explorer on the service page.
 * Package definitions (prices, included parts, coverage zones) come from the
 * server (/api/ppf/config) — the same definitions the calculator, booking
 * snapshot and CRM use. Coverage is shown on a photorealistic 3/4-view render
 * (panels of the selected package highlighted); the checklist below the image
 * is the exact, server-defined coverage.
 */

// "From $X" on the public explorer — X = the lowest class price (D1). The
// exact price for the visitor's vehicle appears in the calculator below.
const pkgFrom = (p) =>
  p.prices ? Math.min(...Object.values(p.prices)) : (p.quote_only ? p.from_price : p.price);

const PKG_IMG = {
  "partial-front": "/ppf/pkg-partial.png",
  "full-front": "/ppf/pkg-full.png",
  "full-front-plus": "/ppf/pkg-plus.png",
  "full-vehicle": "/ppf/pkg-fullbody.png",
};

export default function PpfPackages() {
  const [packages, setPackages] = useState(null);
  const [selKey, setSelKey] = useState("full-front");

  useEffect(() => {
    api.get("/ppf/config").then(({ data }) => setPackages(data.packages)).catch(() => {});
    Object.values(PKG_IMG).forEach((src) => {
      const img = new Image();
      img.src = src;
    });
  }, []);

  if (!packages || packages.length === 0) return null;  // don't flash empty/NaN tabs while the config loads

  const pkg = packages.find((p) => p.key === selKey) || null;

  // Prices render only when they actually exist — never "NaN"/"$0" while the
  // config is loading or if a package price is missing.
  const priceLabel = (p) => {
    const v = pkgFrom(p);
    return Number.isFinite(v) && v > 0 ? `From $${v.toLocaleString("en-CA")}` : "Custom quote";
  };

  return (
    <section className="py-12 border-t border-white/5" data-testid="ppf-packages">
      <PpfPopular />
      <div className="font-mono-alt text-[10px] uppercase tracking-widest text-[#8db2ff] mb-2">Coverage Levels</div>
      <h2 className="font-display text-3xl md:text-4xl mb-6">Choose your coverage</h2>

      <div className="flex flex-wrap gap-2 mb-6" role="tablist" aria-label="PPF packages">
        {(packages || []).map((p) => (
          <button key={p.key} onClick={() => setSelKey(p.key)} role="tab" aria-selected={selKey === p.key}
            data-testid={`ppf-explorer-pkg-${p.key}`}
            className={`px-4 py-3 rounded-xl border text-left transition-colors ${
              selKey === p.key
                ? "bg-[#1F5EFF]/20 border-[#1F5EFF] text-white"
                : "border-white/10 text-white/60 hover:border-white/25 hover:text-white/85"
            }`}>
            <div className="font-mono-alt text-[9px] uppercase tracking-widest text-[#8db2ff]">{p.tag}</div>
            <div className="font-display text-base md:text-lg mt-0.5">{p.name}</div>
            <div className="font-mono-alt text-[10px] mt-1 text-white/50" data-testid={`ppf-explorer-price-${p.key}`}>
              {priceLabel(p)}
            </div>
          </button>
        ))}
      </div>

      <AnimatePresence mode="wait">
        {pkg && (
          <motion.div key={pkg.key}
            initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.25 }}>
            <div className="rounded-xl border border-white/10 bg-black/30 overflow-hidden" data-testid="ppf-diagram">
              <motion.img
                key={pkg.key}
                src={PKG_IMG[pkg.key]}
                alt={`${pkg.name} PPF package — covered panels highlighted`}
                className="w-full h-auto block"
                initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.35 }}
                data-testid="ppf-pkg-image" />
            </div>
            <p className="mt-2 text-[10px] text-white/30">
              Illustrative vehicle. Blue areas represent PPF coverage. Actual coverage varies by vehicle and selected package.
            </p>

            <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6 items-start">
              <div>
                <div className="font-mono-alt text-[10px] uppercase tracking-widest text-white/35 mb-3">
                  Covered by {pkg.name} — exactly:
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                  {(pkg.zones || []).map((z) => (
                    <div key={z} className="flex items-center gap-2 text-sm text-white/80"
                      data-testid={`ppf-zone-${z}`}>
                      <Check className="w-3.5 h-3.5 text-[#8db2ff] shrink-0" />
                      {ZONE_LABELS[z] || z}
                    </div>
                  ))}
                  {pkg.quote_only && (
                    <div className="text-sm text-white/60 sm:col-span-2">
                      …every painted exterior panel. Windshield film, glass and unpainted trim are separate options.
                    </div>
                  )}
                </div>
              </div>
              <div>
                <p className="text-sm text-white/55 leading-relaxed">{pkg.blurb}</p>
                <a
                  href="/calculator?cat=ppf"
                  data-testid="ppf-pkg-cta"
                  className="group mt-4 inline-flex items-center gap-3 px-7 py-4 rounded-full bg-[#1F5EFF] text-white font-semibold hover:bg-white hover:text-black transition-colors"
                >
                  Build &amp; Book — {pkg.name}
                  <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                </a>
                <p className="mt-3 text-[11px] text-white/35">
                  CAD before GST. Veltik Clear PPF PRO 60 (7.5 mil). Need specific panels only? Build a custom set in the calculator.
                </p>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
